/**
 * CGVPage - Conditions Générales de Vente
 * Droit belge - Code de droit économique (Livre VI)
 */

import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useTenant } from '../../contexts/TenantContext';

export default function CGVPage() {
  const navigate = useNavigate();
  const { tenant } = useTenant();
  // Contrôle visuel des fruits et légumes à la préparation (12/10/2026)
  const controleVisuel = (tenant?.config?.ecommerce as any)?.preparation?.controleVisuel ?? {};
  const controleActif = controleVisuel.actif !== false;
  const conservationPhotos = Number(controleVisuel.conservationJours) || 15;
  // Des CGV engagent : tant que la ligne téléphonique n'est pas ouverte, on
  // n'annonce pas l'appel personnalisé comme un service dû.
  const voiceAssistEnabled = tenant?.config?.ecommerce?.voiceAssistEnabled ?? false;
  // Tant que la livraison n'est pas ouverte, les CGV n'en parlent pas : elles
  // engagent, et annoncer un service qu'on ne rend pas est une promesse de trop.
  const deliveryEnabled = tenant?.config?.ecommerce?.deliveryEnabled ?? true;

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/shop')}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ArrowLeft className="w-6 h-6 text-gray-600" />
          </button>
          <h1 className="text-xl font-bold text-gray-800">Conditions Générales de Vente</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <p className="text-sm text-gray-600 mb-8">Dernière mise à jour : 24 octobre 2026</p>

        {/* Article 1 */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Article 1 — Identité du vendeur</h2>
          <div className="prose prose-lg text-gray-700 leading-relaxed">
            <p><strong>Les Folies Maraîchères SRL</strong></p>
            <p>Rue du Commerce 147<br />7370 Dour<br />Belgique</p>
            <p>Tél. : 0478/51.68.22<br />
            Email : contact@foliesboutik.be<br />
            T.V.A. 0715954426 — RPM Mons</p>
            <p>
              Certification biologique : <strong>Certisys</strong> — organisme de contrôle
              <strong> BE-BIO-01</strong>, agréé conformément au Règlement (UE) 2018/848.<br />
              Les Folies Maraîchères portent par ailleurs la mention <strong>Nature &amp; Progrès</strong>,
              qui est une charte associative et non un organisme certificateur.
            </p>
          </div>
        </section>

        {/* Article 2 */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Article 2 — Champ d'application</h2>
          <div className="prose prose-lg text-gray-700 leading-relaxed">
            <p>
              Les présentes Conditions Générales de Vente (ci-après « CGV ») s'appliquent à toute commande passée
              via le site <strong>foliesboutik.be</strong> (ci-après « le Site ») par un consommateur (ci-après « le Client »).
            </p>
            <p>
              En passant commande, le Client reconnaît avoir pris connaissance des présentes CGV et les accepte sans réserve.
              Les CGV applicables sont celles en vigueur au jour de la commande.
            </p>
          </div>
        </section>

        {/* Article 3 */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Article 3 — Produits</h2>
          <div className="prose prose-lg text-gray-700 leading-relaxed">
            <p>
              Les Folies Maraîchères proposent à la vente des produits alimentaires issus de l'agriculture biologique :
              légumes de production propre, fruits, épicerie, produits laitiers, boulangerie et autres denrées alimentaires.
            </p>
            <p>
              Les photographies des produits présentées sur le Site sont fournies à titre indicatif et ne sont pas contractuelles.
              Les produits sont sujets à la disponibilité saisonnière et aux arrivages.
            </p>
            <p>
              Les quantités disponibles ne sont pas affichées sur le Site : nos récoltes et nos arrivages varient d'un
              jour à l'autre, et un chiffre serait faux dès la vente suivante. La disponibilité de chaque article est
              vérifiée au moment de la commande, avant tout paiement : si un produit ne peut être servi en totalité,
              le Client en est informé à cet instant, article par article. En cas d'indisponibilité constatée après
              validation, le Client est prévenu dans les meilleurs délais et peut choisir un produit de substitution
              ou obtenir un remboursement.
            </p>
            <p>
              Pour les produits vendus au poids (fruits, légumes…), le montant facturé est ajusté au poids réellement
              pesé lors de la préparation de la commande. La différence éventuelle par rapport au montant estimé lors
              de la commande est régularisée (complément ou remboursement, le cas échéant en FOLIES — voir Article 7).
            </p>
          </div>
        </section>

        {/* Article 4 */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Article 4 — Prix</h2>
          <div className="prose prose-lg text-gray-700 leading-relaxed">
            <p>
              Les prix sont indiqués en euros, toutes taxes comprises (TTC). Les taux de TVA applicables sont
              conformes à la législation belge :
            </p>
            <ul>
              <li><strong>6 %</strong> : denrées alimentaires</li>
              <li><strong>21 %</strong> : produits non alimentaires (hygiène, maison)</li>
              <li><strong>0 %</strong> : produits exonérés</li>
            </ul>
            <p>
              Les prix peuvent être modifiés à tout moment. Les produits sont facturés sur la base des tarifs en vigueur
              au moment de la validation de la commande. Des frais de livraison peuvent s'appliquer et sont indiqués
              lors du processus de commande.
            </p>
            <p>
              Certains contenants (bouteilles de jus, bocaux de yaourt…) sont consignés. Le montant de la consigne
              est indiqué sur la fiche du produit, apparaît sur une ligne distincte du panier et s'ajoute au prix de
              l'article ; il n'est pas soumis à la TVA. La consigne est remboursée au magasin lorsque vous rapportez
              le contenant vide. Si un article consigné ne peut être fourni, sa consigne vous est remboursée avec lui.
            </p>
            <p>
              Lors de votre commande, vous pouvez indiquer les vidanges consignées que vous rapporterez au retrait.
              Elles sont recomptées avec vous au magasin : seules les vidanges effectivement rendues, propres et en
              bon état, sont reprises, et leur consigne est alors créditée sur votre compte FOLIES. Le montant
              indiqué au moment de la commande est une estimation ; il n'est pas déduit du prix de la commande.
            </p>
          </div>
        </section>

        {/* Article 5 */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Article 5 — Commande</h2>
          <div className="prose prose-lg text-gray-700 leading-relaxed">
            <p>Le processus de commande se déroule comme suit :</p>
            <ol>
              <li>Sélection des produits et ajout au panier</li>
              <li>{deliveryEnabled ? 'Choix du mode de retrait (en magasin) ou de livraison (à domicile)' : 'Retrait en magasin'}</li>
              <li>Sélection du jour de retrait souhaité</li>
              <li>Choix du mode de paiement</li>
              <li>Acceptation des CGV et de la Politique de confidentialité</li>
              <li>Confirmation de la commande</li>
            </ol>
            <p>
              La commande n'est définitivement validée qu'après confirmation du paiement, sauf pour
              les Clients autorisés au paiement au retrait (Article 6), dont la commande est validée
              dès sa confirmation.
              Un email de confirmation est envoyé au Client récapitulant les détails de la commande.
              Un montant minimum de commande peut s'appliquer ; il est indiqué lors du processus de commande.
            </p>
          </div>
        </section>

        {/* Article 6 */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Article 6 — Paiement</h2>
          <div className="prose prose-lg text-gray-700 leading-relaxed">
            <p>
              <strong>Les commandes sont payables d'avance.</strong> Aucune commande n'est enregistrée
              ni préparée sans paiement préalable, et aucun paiement n'est accepté au moment du retrait,
              sauf pour les Clients que Les Folies Maraîchères ont expressément autorisés au paiement au retrait.
            </p>
            <p>
              <strong>Paiement au retrait (Clients autorisés).</strong> Le Client autorisé peut choisir de
              régler sa commande au comptoir, au moment du retrait, en espèces, en chèques repas
              électroniques ou par Bancontact. Le montant dû est celui de la commande préparée, les
              produits vendus au poids étant pesés à la préparation. Les chèques repas ne peuvent régler
              que les denrées alimentaires ; le solde éventuel se règle par un autre moyen. Cette
              autorisation est accordée et peut être retirée à tout moment par Les Folies Maraîchères.
              Une commande non retirée au jour convenu peut être annulée et ses produits remis à la vente.
            </p>
            <p>Les modes de paiement acceptés sont :</p>
            <ul>
              <li><strong>Carte bancaire et Bancontact</strong> : paiement sécurisé via Stripe (Visa, Mastercard, cartes internationales, Bancontact, Apple Pay / Google Pay)</li>
              <li><strong>Compte FOLIES</strong> : solde prépayé du compte client, utilisable sur le Site comme en boutique (voir Article 7)</li>
            </ul>
            <p>
              La commande n'est confirmée qu'à réception effective du paiement. Une commande dont le
              paiement n'aboutit pas est automatiquement annulée et les produits sont remis à la vente.
            </p>
            <p>
              Les paiements par carte bancaire sont sécurisés par le prestataire Stripe, certifié PCI-DSS.
              Les Folies Maraîchères n'ont jamais accès aux données bancaires du Client.
            </p>
          </div>
        </section>

        {/* Article 7 — Compte FOLIES */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Article 7 — Compte FOLIES (compte prépayé)</h2>
          <div className="prose prose-lg text-gray-700 leading-relaxed">
            <p>
              Le compte FOLIES est un compte prépayé personnel, lié au compte client, permettant de régler
              tout ou partie des commandes passées sur le Site. Le solde est exprimé en FOLIES :
              <strong> 1 FOLIE = 1 euro</strong>.
            </p>
            <p><strong>Alimentation du compte :</strong></p>
            <ul>
              <li>Par carte bancaire ou Bancontact via Stripe, depuis la page « Mes FOLIES » (montant minimum de 5 €, maximum de 500 € par recharge) ou directement au moment de payer une commande, pour compléter un solde insuffisant (complément exact, minimum 1 €)</li>
              <li>Par crédit effectué par Les Folies Maraîchères dans le cadre d'un remboursement ou d'un geste commercial</li>
            </ul>
            <p><strong>Utilisation :</strong></p>
            <ul>
              <li>
                Le solde FOLIES est utilisable <strong>sur le Site</strong> et <strong>en boutique</strong>,
                à Elouges comme sur nos marchés : à la caisse, il suffit d'indiquer votre compte client
                au vendeur. Un achat en boutique peut être réglé en tout ou partie en FOLIES, le solde
                éventuel par un autre moyen de paiement
              </li>
              <li>Il n'est utilisable qu'auprès des Folies Maraîchères</li>
              <li>Il est personnel et non transférable à un tiers</li>
              <li>Il ne produit pas d'intérêts</li>
              <li>Il n'a pas de date d'expiration</li>
            </ul>
            <p><strong>Remboursement :</strong></p>
            <p>
              Le Client peut demander à tout moment le remboursement de tout ou partie de son solde FOLIES,
              par simple demande à contact@foliesboutik.be. Le remboursement est effectué dans un délai maximum
              de 30 jours, via le moyen de paiement utilisé pour l'alimentation du compte ou par virement bancaire.
              En cas de clôture du compte client, le solde restant est intégralement remboursé.
            </p>
          </div>
        </section>

        {/* Article 8 */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Article 8 — Droit de rétractation</h2>
          <div className="bg-amber-50 border-l-4 border-amber-400 p-6 rounded-r-lg mb-4">
            <p className="text-amber-900 font-medium">
              Conformément à l'article VI.53, 4° du Code de droit économique belge, le droit de rétractation
              ne s'applique pas aux contrats de fourniture de denrées alimentaires périssables ou de courte
              durée de conservation.
            </p>
          </div>
          <div className="prose prose-lg text-gray-700 leading-relaxed">
            <p>
              La quasi-totalité de nos produits étant des denrées alimentaires périssables (légumes, fruits, produits frais,
              produits laitiers, boulangerie), le Client ne bénéficie pas du droit de rétractation de 14 jours
              prévu par la législation sur les ventes à distance.
            </p>
            <p>
              Pour les produits non périssables (épicerie, boissons, fruits secs, produits d'hygiène et d'entretien,
              bacs Folies), le Client dispose d'un délai de 14 jours à compter du retrait de sa commande pour se
              rétracter, sans avoir à motiver sa décision. Il en informe Les Folies Maraîchères par email
              (contact@foliesboutik.be) ou au magasin, et rapporte le produit au magasin, sans frais, dans ce même
              délai. Le remboursement intervient au plus tard 14 jours après la réception du produit rapporté, par
              le moyen de paiement utilisé lors de la commande.
            </p>
            <p>
              Ne sont pas repris les produits scellés qui ont été descellés et ne peuvent être renvoyés pour des
              raisons de protection de la santé ou d'hygiène (article VI.53, 5° du Code de droit économique). Le
              Client répond de la dépréciation d'un produit résultant de manipulations autres que celles nécessaires
              pour en établir la nature et les caractéristiques.
            </p>
            <p>
              Toutefois, en cas de produit non conforme, endommagé ou manquant, le Client peut nous contacter
              dans les 24 heures suivant la réception pour obtenir un remboursement ou un remplacement
              (voir Article 11 — Réclamations).
            </p>
          </div>
        </section>

        {/* Article 9 */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Article 9 — Livraison et retrait</h2>
          <div className="prose prose-lg text-gray-700 leading-relaxed">
            <p><strong>Retrait en magasin :</strong></p>
            <ul>
              <li>Gratuit</li>
              <li>Les Folies Maraîchères, Rue du Commerce 147, 7370 Dour</li>
              <li>Au jour de retrait choisi lors de la commande, parmi les jours d'ouverture proposés</li>
              <li>
                Heures d'ouverture : jeudi de 14h à 18h, vendredi de 10h à 18h, samedi de 10h à 16h.
                {' '}<strong>Le dimanche, le retrait se fait au marché de Mons, place Nervienne, à partir de 7h30</strong> —
                et non à la ferme.
              </li>
              <li>Le Client est prévenu dès que sa commande est prête ; il la retire ce jour-là, aux heures indiquées ci-dessus</li>
            </ul>
            {deliveryEnabled ? (
              <>
                <p><strong>Livraison à domicile :</strong></p>
                <ul>
                  <li>Zone de livraison : périmètre défini autour d'Elouges (indiqué lors de la commande)</li>
                  <li>Frais de livraison : indiqués lors de la commande</li>
                  <li>Délai : selon le créneau choisi par le Client</li>
                </ul>
                <p>
                  En cas d'absence lors de la livraison, le Client sera contacté pour convenir d'un nouveau créneau.
                </p>
              </>
            ) : (
              <p>
                <strong>Livraison à domicile :</strong> ce service n'est pas proposé actuellement.
                Les commandes passées sur le Site sont exclusivement retirées en magasin.
              </p>
            )}
            <p>
              Les produits non récupérés dans les deux jours ouvrables suivant le jour de retrait convenu
              ne pourront faire l'objet d'un remboursement, s'agissant de denrées périssables.
            </p>
            {/* Bacs Folies — décision du 25/09/2026, bacs reçus le 13/10/2026 */}
            <p><strong>Bacs Folies :</strong></p>
            <ul>
              <li>
                Le bac Folies (bac pliable de 45 litres, marqué au logo des Folies Maraîchères) est vendu au prix
                indiqué sur sa fiche. Il n'est pas consigné : son prix n'est pas remboursé lorsque le bac est rapporté.
              </li>
              <li>
                Au retrait d'une commande, le Client peut rapporter ses bacs Folies vides : chaque bac vide, propre et
                en bon état est échangé, un pour un, contre un bac plein de sa commande. Le nombre de bacs rapportés
                peut être indiqué lors de la commande.
              </li>
              <li>
                Seuls les bacs Folies sont échangés. Un bac cassé, fendu ou sale n'est pas échangé ; le Client peut
                alors en acheter un nouveau.
              </li>
              <li>
                Si le Client n'a pas de bac Folies, sa commande lui est remise dans des caisses de fruits et légumes
                réutilisées, en carton ou en bois, ou dans un bac Folies qu'il achète.
              </li>
              <li>
                Un bac acheté sur le Site, neuf et non utilisé, bénéficie du droit de rétractation de 14 jours prévu
                par le Code de droit économique (voir Article 8).
              </li>
            </ul>
          </div>
        </section>

        {/* Article 10 */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Article 10 — Appel personnalisé</h2>
          <div className="prose prose-lg text-gray-700 leading-relaxed">
            {voiceAssistEnabled ? (
              <p>
                Le Client peut demander un appel téléphonique gratuit pendant la préparation de sa commande.
                Un préparateur contacte le Client pour discuter des substitutions possibles, des suggestions du jour
                et des préférences personnelles. Ce service est optionnel et sans engagement.
              </p>
            ) : (
              <p>
                L'appel personnalisé pendant la préparation n'est pas encore proposé. Il sera introduit
                ultérieurement et signalé au moment de la commande. En attendant, le Vendeur peut contacter
                le Client par téléphone si sa commande nécessite une substitution.
              </p>
            )}
          </div>
        </section>

        {/* Article 11 */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Article 11 — Réclamations et service après-vente</h2>
          <div className="prose prose-lg text-gray-700 leading-relaxed">
            <p>
              Pour toute réclamation concernant un produit non conforme, endommagé ou manquant, le Client
              peut nous contacter :
            </p>
            <ul>
              <li>Par email : contact@foliesboutik.be</li>
              <li>Par téléphone : 0478/51.68.22</li>
            </ul>
            <p>
              Les réclamations doivent être formulées dans les <strong>24 heures</strong> suivant la réception
              de la commande, accompagnées si possible d'une photo du produit concerné.
            </p>
            <p>
              Après vérification, nous proposerons au Client un remboursement du produit concerné (via le moyen
              de paiement initial ou en crédit FOLIES sur son compte — voir Article 7) ou un remplacement lors
              de la prochaine commande.
            </p>
            {controleActif && (
              <p>
                Lors de la préparation, les fruits et légumes pesés sont photographiés sur la balance et
                comparés à l'article commandé. Ces photos servent à vérifier la préparation en cas de
                réclamation ; elles sont conservées <strong>{conservationPhotos} jours</strong> puis effacées
                automatiquement (voir notre politique de confidentialité).
              </p>
            )}
          </div>
        </section>

        {/* Article 12 */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Article 12 — Responsabilité</h2>
          <div className="prose prose-lg text-gray-700 leading-relaxed">
            <p>
              Les Folies Maraîchères s'engagent à fournir des produits conformes à la description présentée sur le Site
              et aux normes de l'agriculture biologique (certification Certisys, organisme de contrôle BE-BIO-01).
            </p>
            <p>
              La responsabilité du vendeur ne saurait être engagée en cas de :
            </p>
            <ul>
              <li>Force majeure ou événement indépendant de sa volonté</li>
              <li>Mauvaise utilisation ou conservation inappropriée du produit par le Client</li>
              <li>Indisponibilité temporaire du Site pour maintenance</li>
            </ul>
          </div>
        </section>

        {/* Article 13 */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Article 13 — Données personnelles</h2>
          <div className="prose prose-lg text-gray-700 leading-relaxed">
            <p>
              Les données personnelles collectées lors de la commande sont traitées conformément au Règlement
              Général sur la Protection des Données (RGPD) et à notre{' '}
              <button
                onClick={() => navigate('/shop/privacy')}
                className="text-green-700 hover:text-green-700 underline font-medium"
              >
                Politique de confidentialité
              </button>.
            </p>
          </div>
        </section>

        {/* Article 14 */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Article 14 — Droit applicable et litiges</h2>
          <div className="prose prose-lg text-gray-700 leading-relaxed">
            <p>
              Les présentes CGV sont soumises au droit belge. En cas de litige, les parties s'engagent à rechercher
              une solution amiable. À défaut, les tribunaux de l'arrondissement judiciaire du Hainaut seront seuls compétents.
            </p>
            <p>
              Le Client peut également s'adresser au Service de Médiation pour le Consommateur, service public
              fédéral belge : <span className="text-green-700 font-medium">mediationconsommateur.be</span> —
              Boulevard du Roi Albert II 8, 1000 Bruxelles.
              {/* La plateforme européenne de règlement en ligne des litiges (ec.europa.eu/consumers/odr) a été
                  FERMÉE le 20 juillet 2025 : y renvoyer laissait le client sans recours identifié. */}
            </p>
          </div>
        </section>

        {/* Article 15 */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Article 15 — Modification des CGV</h2>
          <div className="prose prose-lg text-gray-700 leading-relaxed">
            <p>
              Les Folies Maraîchères se réservent le droit de modifier les présentes CGV à tout moment.
              Les CGV applicables sont celles en vigueur au moment de la passation de la commande par le Client.
            </p>
          </div>
        </section>

        {/* Bouton retour */}
        <div className="text-center mt-12">
          <button
            onClick={() => navigate('/shop')}
            className="px-8 py-3 bg-green-600 text-white font-semibold rounded-lg transition-opacity hover:opacity-90"
          >
            Retour à la boutique
          </button>
        </div>
      </main>

    </div>
  );
}
